import React, { useState, useEffect, useMemo, useRef } from 'react';
import './RegistrationForm.css';
import { saveEntity } from '../utils/storage';
import { loadProfileDraft, saveProfileDraft, clearProfileDraft } from '../utils/profileDraft';
import {
  sanitizeText,
  sanitizeUrl,
  validateEmail,
  validatePhone,
  validateLength,
  checkRateLimit,
  validateFileType,
  validateFileSize,
  sanitizeFormData
} from '../utils/security';
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaPinterest,
  FaSnapchat,
  FaTiktok,
  FaReddit,
  FaGithub,
  FaDribbble,
  FaBehance,
  FaTelegram,
  FaWhatsapp,
  FaDiscord,
  FaTwitch,
  FaVimeo,
  FaFlickr,
  FaImage,
  FaTimes,
  FaUserPlus,
  FaExternalLinkAlt,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import { SiGooglemaps } from 'react-icons/si';
import CustomLinkIcon from './CustomLinkIcon';
import {
  NONPROFIT_ICONS,
  toPresetIconValue,
  isPresetIcon,
} from '../utils/nonprofitIcons';

const socialMediaPlatforms = [
  { name: 'Twitter', icon: FaTwitter, color: '#1da1f2', defaultDomain: 'https://twitter.com/', placeholder: 'https://twitter.com/yourhandle', signupUrl: 'https://twitter.com/i/flow/signup' },
  { name: 'Instagram', icon: FaInstagram, color: '#e4405f', defaultDomain: 'https://instagram.com/', placeholder: 'https://instagram.com/yourprofile', signupUrl: 'https://www.instagram.com/accounts/emailsignup/' },
  { name: 'Google Maps Reviews', icon: SiGooglemaps, color: '#4285F4', defaultDomain: 'https://www.google.com/maps/', placeholder: 'https://g.page/r/your-business/review', signupUrl: 'https://business.google.com/' },
  { name: 'Facebook', icon: FaFacebook, color: '#1877f2', defaultDomain: 'https://facebook.com/', placeholder: 'https://facebook.com/yourpage', signupUrl: 'https://www.facebook.com/r.php' },
  { name: 'YouTube', icon: FaYoutube, color: '#ff0000', defaultDomain: 'https://youtube.com/', placeholder: 'https://youtube.com/yourchannel', signupUrl: 'https://accounts.google.com/signup/v2/webcreateaccount?flowName=GlifWebSignIn&flowEntry=SignUp' },
  { name: 'TikTok', icon: FaTiktok, color: '#000000', defaultDomain: 'https://tiktok.com/@', placeholder: 'https://tiktok.com/@yourhandle', signupUrl: 'https://www.tiktok.com/signup' },
  { name: 'Snapchat', icon: FaSnapchat, color: '#fffc00', defaultDomain: 'https://snapchat.com/add/', placeholder: 'https://snapchat.com/add/yourusername', signupUrl: 'https://accounts.snapchat.com/accounts/signup' },
  { name: 'WhatsApp', icon: FaWhatsapp, color: '#25d366', defaultDomain: 'https://wa.me/', placeholder: 'https://wa.me/yournumber', signupUrl: 'https://www.whatsapp.com/download' },
  { name: 'LinkedIn', icon: FaLinkedin, color: '#0077b5', defaultDomain: 'https://linkedin.com/', placeholder: 'https://linkedin.com/company/yourcompany', signupUrl: 'https://www.linkedin.com/signup' },
  { name: 'Pinterest', icon: FaPinterest, color: '#bd081c', defaultDomain: 'https://pinterest.com/', placeholder: 'https://pinterest.com/yourprofile', signupUrl: 'https://www.pinterest.com/join/' },
  { name: 'Telegram', icon: FaTelegram, color: '#0088cc', defaultDomain: 'https://t.me/', placeholder: 'https://t.me/yourchannel', signupUrl: 'https://telegram.org/' },
  { name: 'Reddit', icon: FaReddit, color: '#ff4500', defaultDomain: 'https://reddit.com/user/', placeholder: 'https://reddit.com/user/yourusername', signupUrl: 'https://www.reddit.com/register' },
  { name: 'Discord', icon: FaDiscord, color: '#5865f2', defaultDomain: 'https://discord.gg/', placeholder: 'https://discord.gg/yourserver', signupUrl: 'https://discord.com/register' },
  { name: 'Twitch', icon: FaTwitch, color: '#9146ff', defaultDomain: 'https://twitch.tv/', placeholder: 'https://twitch.tv/yourchannel', signupUrl: 'https://www.twitch.tv/signup' },
  { name: 'GitHub', icon: FaGithub, color: '#181717', defaultDomain: 'https://github.com/', placeholder: 'https://github.com/yourusername', signupUrl: 'https://github.com/signup' },
  { name: 'Dribbble', icon: FaDribbble, color: '#ea4c89', defaultDomain: 'https://dribbble.com/', placeholder: 'https://dribbble.com/yourprofile', signupUrl: 'https://dribbble.com/signup/new' },
  { name: 'Behance', icon: FaBehance, color: '#1769ff', defaultDomain: 'https://behance.net/', placeholder: 'https://behance.net/yourprofile', signupUrl: 'https://www.behance.net/signup' },
  { name: 'Vimeo', icon: FaVimeo, color: '#1ab7ea', defaultDomain: 'https://vimeo.com/', placeholder: 'https://vimeo.com/yourprofile', signupUrl: 'https://vimeo.com/join' },
  { name: 'Flickr', icon: FaFlickr, color: '#ff0084', defaultDomain: 'https://flickr.com/photos/', placeholder: 'https://flickr.com/photos/yourprofile', signupUrl: 'https://www.flickr.com/signup' },
];

// Initialize default social media URLs
const getInitialSocialMedia = () => {
  const initialSocial = {};
  socialMediaPlatforms.forEach((platform) => {
    initialSocial[platform.name.toLowerCase()] = platform.defaultDomain;
  });
  return initialSocial;
};

const createEmptyCustomLink = () => ({
  clientId: typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `link-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: '',
  icon: null,
  link: '',
  showTextOnPage: false,
});

/** Existing saved links when editing; empty for new profiles until the user adds one. */
const normalizeCustomLinks = (links) => {
  if (!Array.isArray(links) || links.length === 0) {
    return [];
  }
  return links.map((link) => ({
    clientId: link.clientId || (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `link-${Date.now()}-${Math.random().toString(36).slice(2)}`),
    name: link.name || '',
    icon: link.icon ?? null,
    link: link.link || '',
    showTextOnPage: Boolean(link.showTextOnPage),
  }));
};

const socialHasCustomUrl = (platform, url) => {
  const value = (url || '').trim();
  return Boolean(value && value !== platform.defaultDomain);
};

const getFilledSocialKeys = (socialMedia) =>
  socialMediaPlatforms
    .filter((platform) => socialHasCustomUrl(platform, socialMedia?.[platform.name.toLowerCase()]))
    .map((platform) => platform.name.toLowerCase());

const getOptionalOpenState = (data) => ({
  logo: Boolean(data?.logo),
  description: Boolean(data?.description),
  website: Boolean(data?.website),
  phone: Boolean(data?.phone),
  contact: Boolean(data?.contactPersonName || data?.contactPersonEmail || data?.contactPersonPhone),
  location: Boolean(data?.address || data?.city || data?.country),
  social: getFilledSocialKeys(data?.socialMedia).length > 0,
  customLinks: Boolean(
    data?.customLinksAboveSocial
    || (data?.customLinks || []).some((link) => link.name || link.icon || link.link)
  ),
});

const hasCompleteSocialLink = (socialMedia) =>
  socialMediaPlatforms.some((platform) => {
    const url = (socialMedia?.[platform.name.toLowerCase()] || '').trim();
    return socialHasCustomUrl(platform, url) && Boolean(sanitizeUrl(url));
  });

const hasCompleteCustomLink = (customLinks) =>
  (customLinks || []).some((link) => {
    const name = (link.name || '').trim();
    const url = (link.link || '').trim();
    return Boolean(name && link.icon && url && sanitizeUrl(url));
  });

const hasRequiredProfileLink = (data) =>
  hasCompleteSocialLink(data?.socialMedia) || hasCompleteCustomLink(data?.customLinks);

function RegistrationForm({ entity, onSave, onCancel, onDirtyChange, currentUser, onLogout }) {
  // Memoize initial form data based on entity
  const initialFormData = useMemo(() => {
    if (entity) {
      // Load entity data for editing - restore default domains for social media
      const socialMedia = { ...getInitialSocialMedia(), ...(entity.socialMedia || {}) };
        return {
          entityName: entity.entityName || '',
          description: entity.description || '',
          email: entity.email || '',
          website: entity.website || '',
          phone: entity.phone || '',
          address: entity.address || '',
          city: entity.city || '',
          country: entity.country || '',
          contactPersonName: entity.contactPersonName || '',
          contactPersonEmail: entity.contactPersonEmail || '',
          contactPersonPhone: entity.contactPersonPhone || '',
          socialMedia: socialMedia,
          logo: entity.logo || null,
          customLinks: normalizeCustomLinks(entity.customLinks),
          customLinksAboveSocial: Boolean(entity.customLinksAboveSocial),
        };
    }
    return {
      entityName: '',
      description: '',
      email: '',
      website: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      contactPersonName: '',
      contactPersonEmail: '',
      contactPersonPhone: '',
      socialMedia: getInitialSocialMedia(),
      logo: null,
      customLinks: normalizeCustomLinks([]),
      customLinksAboveSocial: false,
    };
  }, [entity]);

  const mergeDraft = (draft) => {
    if (!draft) return initialFormData;
    return {
      ...initialFormData,
      ...draft,
      socialMedia: { ...initialFormData.socialMedia, ...(draft.socialMedia || {}) },
      customLinks: normalizeCustomLinks(
        Array.isArray(draft.customLinks) && draft.customLinks.length > 0
          ? draft.customLinks
          : initialFormData.customLinks
      ),
    };
  };

  const [formData, setFormData] = useState(() => mergeDraft(loadProfileDraft(entity?.id || null)));
  const [initialData, setInitialData] = useState(() => JSON.stringify(initialFormData));
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [logoPreview, setLogoPreview] = useState(() => {
    const draft = loadProfileDraft(entity?.id || null);
    return draft?.logo || entity?.logo || null;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dirtyRef = useRef(false);
  const [showOptional, setShowOptional] = useState(() =>
    getOptionalOpenState(mergeDraft(loadProfileDraft(entity?.id || null)))
  );
  const [addedSocial, setAddedSocial] = useState(() =>
    getFilledSocialKeys(mergeDraft(loadProfileDraft(entity?.id || null)).socialMedia)
  );

  const applyDraftOrInitial = (draft) => {
    const restored = draft ? mergeDraft(draft) : initialFormData;
    setFormData(restored);
    setInitialData(JSON.stringify(initialFormData));
    setLogoPreview(restored.logo || entity?.logo || null);
    setShowOptional(getOptionalOpenState(restored));
    setAddedSocial(getFilledSocialKeys(restored.socialMedia));
  };

  // Update form when entity prop changes; restore unsaved draft if present
  useEffect(() => {
    applyDraftOrInitial(loadProfileDraft(entity?.id || null));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate when the profile being edited changes
  }, [initialFormData, entity]);

  const handleCustomLinkChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedLinks = [...prev.customLinks];
      updatedLinks[index] = {
        ...updatedLinks[index],
        [field]: value,
      };
      return {
        ...prev,
        customLinks: updatedLinks,
      };
    });
    // Clear error for this field
    if (errors[`customLink_${index}_${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`customLink_${index}_${field}`];
        return newErrors;
      });
    }
  };

  const handleCustomLinkIconChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type using security utility
      if (!validateFileType(file)) {
        setErrors((prev) => ({
          ...prev,
          [`customLink_${index}_icon`]: 'Please select a valid image file (JPEG, PNG, GIF, or WebP)',
        }));
        return;
      }

      // Validate file size using security utility (max 1MB for icons)
      if (!validateFileSize(file, 1)) {
        setErrors((prev) => ({
          ...prev,
          [`customLink_${index}_icon`]: 'Image size must be less than 1MB',
        }));
        return;
      }

      // Additional security: Check file extension matches MIME type
      const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
      const fileName = file.name.toLowerCase();
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExtension) {
        setErrors((prev) => ({
          ...prev,
          [`customLink_${index}_icon`]: 'File extension does not match file type',
        }));
        return;
      }

      // Read file as data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        const iconDataUrl = reader.result;
        
        // Additional validation: Ensure data URL is valid image
        if (!iconDataUrl.startsWith('data:image/')) {
          setErrors((prev) => ({
            ...prev,
            [`customLink_${index}_icon`]: 'Invalid image file',
          }));
          return;
        }
        
        handleCustomLinkChange(index, 'icon', iconDataUrl);
        setErrors((prev) => ({
          ...prev,
          [`customLink_${index}_icon`]: '',
        }));
      };
      reader.onerror = () => {
        setErrors((prev) => ({
          ...prev,
          [`customLink_${index}_icon`]: 'Error reading file',
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveCustomLinkIcon = (index) => {
    handleCustomLinkChange(index, 'icon', null);
  };

  const handleSelectPresetIcon = (index, presetId) => {
    const preset = NONPROFIT_ICONS.find((item) => item.id === presetId);
    if (!preset) return;

    setFormData((prev) => {
      const updatedLinks = [...prev.customLinks];
      const current = updatedLinks[index];
      const currentName = (current.name || '').trim();
      const matchesPresetLabel = NONPROFIT_ICONS.some((item) => item.label === currentName);
      updatedLinks[index] = {
        ...current,
        icon: toPresetIconValue(presetId),
        name: !currentName || matchesPresetLabel ? preset.label : current.name,
      };
      return {
        ...prev,
        customLinks: updatedLinks,
      };
    });

    if (errors[`customLink_${index}_icon`]) {
      setErrors((prev) => ({
        ...prev,
        [`customLink_${index}_icon`]: '',
      }));
    }
  };

  const handleAddCustomLink = () => {
    setFormData((prev) => ({
      ...prev,
      customLinks: [...prev.customLinks, createEmptyCustomLink()],
    }));
  };

  const handleRemoveCustomLink = (index) => {
    setFormData((prev) => {
      const updatedLinks = prev.customLinks.filter((_, i) => i !== index);
      return {
        ...prev,
        customLinks: updatedLinks,
      };
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith('customLink_')) delete newErrors[key];
      });
      return newErrors;
    });
  };

  const openOptional = (key) => {
    setShowOptional((prev) => ({ ...prev, [key]: true }));
    if (key === 'customLinks') {
      setFormData((prev) => (
        prev.customLinks.length > 0
          ? prev
          : { ...prev, customLinks: [createEmptyCustomLink()] }
      ));
    }
  };

  const hideOptional = (key) => {
    if (key === 'customLinks') {
      setFormData((prev) => ({
        ...prev,
        customLinks: prev.customLinks.filter((link) => link.name || link.icon || link.link),
      }));
    }
    setShowOptional((prev) => ({ ...prev, [key]: false }));
  };

  const addSocialPlatform = (platformKey) => {
    setShowOptional((prev) => ({ ...prev, social: true }));
    setAddedSocial((prev) => (prev.includes(platformKey) ? prev : [...prev, platformKey]));
  };

  const removeSocialPlatform = (platformKey) => {
    const platform = socialMediaPlatforms.find((item) => item.name.toLowerCase() === platformKey);
    handleSocialMediaChange(platformKey, platform?.defaultDomain || '');
    const remaining = addedSocial.filter((key) => key !== platformKey);
    setAddedSocial(remaining);
    if (remaining.length === 0) {
      setShowOptional((open) => ({ ...open, social: false }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Apply length limits based on field
    let sanitizedValue = value;
    const maxLengths = {
      entityName: 100,
      email: 254,
      website: 500,
      phone: 20,
      address: 200,
      city: 100,
      country: 100,
      description: 2000,
      contactPersonName: 100,
      contactPersonEmail: 254,
      contactPersonPhone: 20,
    };
    
    if (maxLengths[name] && value.length > maxLengths[name]) {
      sanitizedValue = value.substring(0, maxLengths[name]);
    }
    
    // Sanitize input based on field type
    if (name === 'entityName' || name === 'address' || name === 'city' || name === 'country') {
      sanitizedValue = sanitizeText(sanitizedValue);
    } else if (name === 'email') {
      sanitizedValue = sanitizeText(sanitizedValue).toLowerCase().trim();
    } else if (name === 'phone' || name === 'contactPersonPhone') {
      sanitizedValue = sanitizeText(sanitizedValue).trim();
    } else if (name === 'contactPersonName') {
      sanitizedValue = sanitizeText(sanitizedValue).trim();
    } else if (name === 'contactPersonEmail') {
      sanitizedValue = sanitizeText(sanitizedValue).toLowerCase().trim();
    } else if (name === 'description') {
      sanitizedValue = sanitizeText(sanitizedValue);
    } else if (name === 'website') {
      // Don't sanitize URL during typing, only validate on blur/submit
      sanitizedValue = value;
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSocialMediaChange = (platform, value) => {
    // Limit URL length
    const maxUrlLength = 500;
    const sanitizedValue = value.length > maxUrlLength ? value.substring(0, maxUrlLength) : value;
    
    setFormData((prev) => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: sanitizedValue,
      },
    }));
    
    // Clear error when user starts typing
    const errorKey = `social_${platform}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({
        ...prev,
        [errorKey]: '',
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type using security utility
      if (!validateFileType(file)) {
        setErrors((prev) => ({
          ...prev,
          logo: 'Please select a valid image file (JPEG, PNG, GIF, or WebP)',
        }));
        return;
      }

      // Validate file size using security utility
      if (!validateFileSize(file, 2)) {
        setErrors((prev) => ({
          ...prev,
          logo: 'Image size must be less than 2MB',
        }));
        return;
      }

      // Additional security: Check file extension matches MIME type
      const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
      const fileName = file.name.toLowerCase();
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExtension) {
        setErrors((prev) => ({
          ...prev,
          logo: 'File extension does not match file type',
        }));
        return;
      }

      // Read file as data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        const logoDataUrl = reader.result;
        
        // Additional validation: Ensure data URL is valid image
        if (!logoDataUrl.startsWith('data:image/')) {
          setErrors((prev) => ({
            ...prev,
            logo: 'Invalid image file',
          }));
          return;
        }
        
        setFormData((prev) => ({
          ...prev,
          logo: logoDataUrl,
        }));
        setLogoPreview(logoDataUrl);
        setErrors((prev) => ({
          ...prev,
          logo: '',
        }));
      };
      reader.onerror = () => {
        setErrors((prev) => ({
          ...prev,
          logo: 'Error reading file',
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData((prev) => ({
      ...prev,
      logo: null,
    }));
    setLogoPreview(null);
    setErrors((prev) => ({
      ...prev,
      logo: '',
    }));
  };

  // Check if form has been modified
  const hasFormChanged = () => {
    // Create a copy without logo for comparison (logo is data URL which can be large)
    const currentDataCopy = { ...formData };
    const initialDataCopy = JSON.parse(initialData);
    // Compare logo separately
    const logoChanged = currentDataCopy.logo !== initialDataCopy.logo;
    delete currentDataCopy.logo;
    delete initialDataCopy.logo;
    const otherFieldsChanged = JSON.stringify(currentDataCopy) !== JSON.stringify(initialDataCopy);
    return otherFieldsChanged || logoChanged;
  };

  dirtyRef.current = !submitted && !isSubmitting && hasFormChanged();

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirtyRef.current);
  }, [formData, initialData, submitted, isSubmitting, onDirtyChange]);

  useEffect(() => {
    if (submitted || isSubmitting) return;
    if (dirtyRef.current) {
      saveProfileDraft(entity?.id || null, formData);
    }
  }, [formData, entity, submitted, isSubmitting]);

  useEffect(() => {
    if (!errors.linksRequired) return;
    if (!hasRequiredProfileLink(formData)) return;
    setErrors((prev) => {
      if (!prev.linksRequired) return prev;
      const next = { ...prev };
      delete next.linksRequired;
      return next;
    });
  }, [formData, errors.linksRequired]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Validate entity name with length check
    if (!formData.entityName.trim()) {
      newErrors.entityName = 'Company/Brand name is required';
    } else if (!validateLength(formData.entityName, 1, 100)) {
      newErrors.entityName = 'Company/Brand name must be between 1 and 100 characters';
    }

    // Validate email using security utility
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate website URL with security checks
    if (formData.website && formData.website.trim()) {
      const sanitizedUrl = sanitizeUrl(formData.website);
      if (!sanitizedUrl) {
        newErrors.website = 'Please enter a valid URL (HTTP or HTTPS only)';
      } else if (!validateLength(formData.website, 0, 500)) {
        newErrors.website = 'URL must be less than 500 characters';
      }
    }

    // Validate phone
    if (formData.phone && formData.phone.trim() && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Validate contact person fields (optional)
    if (formData.contactPersonName && formData.contactPersonName.trim()) {
      if (!validateLength(formData.contactPersonName, 1, 100)) {
        newErrors.contactPersonName = 'Contact person name must be between 1 and 100 characters';
      }
    }

    if (formData.contactPersonEmail && formData.contactPersonEmail.trim()) {
      if (!validateEmail(formData.contactPersonEmail)) {
        newErrors.contactPersonEmail = 'Please enter a valid email address for contact person';
      }
    }

    if (formData.contactPersonPhone && formData.contactPersonPhone.trim()) {
      if (!validatePhone(formData.contactPersonPhone)) {
        newErrors.contactPersonPhone = 'Please enter a valid phone number for contact person';
      }
    }

    // Validate text fields length
    if (formData.description && !validateLength(formData.description, 0, 2000)) {
      newErrors.description = 'Description must be less than 2000 characters';
    }
    
    if (formData.address && !validateLength(formData.address, 0, 200)) {
      newErrors.address = 'Address must be less than 200 characters';
    }
    
    if (formData.city && !validateLength(formData.city, 0, 100)) {
      newErrors.city = 'City must be less than 100 characters';
    }
    
    if (formData.country && !validateLength(formData.country, 0, 100)) {
      newErrors.country = 'Country must be less than 100 characters';
    }

    // Validate social media URLs - only check if they differ from default domain
    Object.keys(formData.socialMedia).forEach((platform) => {
      const url = formData.socialMedia[platform];
      const platformData = socialMediaPlatforms.find(p => p.name.toLowerCase() === platform);
      // Only validate if URL is different from default domain (user has modified it)
      if (url && url !== platformData?.defaultDomain) {
        const sanitizedUrl = sanitizeUrl(url);
        if (!sanitizedUrl) {
          newErrors[`social_${platform}`] = `Please enter a valid URL for ${platform} (HTTP or HTTPS only)`;
        } else if (!validateLength(url, 0, 500)) {
          newErrors[`social_${platform}`] = `URL must be less than 500 characters`;
        }
      }
    });

    // Validate custom links (optional - only validate if user starts filling them)
    formData.customLinks.forEach((customLink, index) => {
      // If any field is filled, validate the filled fields
      const hasAnyField = customLink.name || customLink.icon || customLink.link;
      if (hasAnyField) {
        // Name validation (only if filled)
        if (customLink.name && customLink.name.trim()) {
          if (!validateLength(customLink.name, 1, 100)) {
            newErrors[`customLink_${index}_name`] = 'Name must be between 1 and 100 characters';
          }
        }
        
        // Link validation (only if filled)
        if (customLink.link && customLink.link.trim()) {
          const sanitizedUrl = sanitizeUrl(customLink.link);
          if (!sanitizedUrl) {
            newErrors[`customLink_${index}_link`] = 'Please enter a valid URL (HTTP or HTTPS only)';
          } else if (!validateLength(customLink.link, 0, 500)) {
            newErrors[`customLink_${index}_link`] = 'URL must be less than 500 characters';
          }
        }
        
        // If user fills name or link, they should fill all fields for that link
        if ((customLink.name && customLink.name.trim()) || (customLink.link && customLink.link.trim())) {
          if (!customLink.name || !customLink.name.trim()) {
            newErrors[`customLink_${index}_name`] = 'Name is required if you fill other fields';
          }
          if (!customLink.icon) {
            newErrors[`customLink_${index}_icon`] = 'Icon image is required if you fill other fields';
          }
          if (!customLink.link || !customLink.link.trim()) {
            newErrors[`customLink_${index}_link`] = 'Link is required if you fill other fields';
          }
        }
      }
    });

    if (!hasRequiredProfileLink(formData)) {
      newErrors.linksRequired = 'Add at least one social media profile or custom link';
    }

    setErrors(newErrors);
    if (newErrors.linksRequired) {
      setShowOptional((prev) => ({ ...prev, social: true }));
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    // Rate limiting check
    const rateLimitCheck = checkRateLimit('form_submission', 20, 15);
    if (!rateLimitCheck.allowed) {
      setErrors({
        general: `Too many submissions. Please wait ${rateLimitCheck.timeRemaining} minute(s) before trying again.`
      });
      return;
    }
    
    // Check if form has been modified
    if (!hasFormChanged()) {
      setErrors({ general: 'Please update at least one field before submitting.' });
      return;
    }

    if (validateForm()) {
      setIsSubmitting(true);
      
      // Sanitize all form data before saving
      const sanitizedFormData = sanitizeFormData(formData);
      
      // Filter out default domains from social media before saving
      const socialMediaToSave = {};
      Object.keys(sanitizedFormData.socialMedia).forEach((platform) => {
        const url = sanitizedFormData.socialMedia[platform];
        const platformData = socialMediaPlatforms.find(p => p.name.toLowerCase() === platform);
        // Only save if URL is different from default domain and is valid
        if (url && url !== platformData?.defaultDomain) {
          const sanitizedUrl = sanitizeUrl(url);
          if (sanitizedUrl) {
            socialMediaToSave[platform] = sanitizedUrl;
          }
        }
      });

      // Filter out empty custom links
      const customLinksToSave = (sanitizedFormData.customLinks || [])
        .filter((link) => link.name && link.name.trim() && link.icon && link.link && link.link.trim())
        .map(({ clientId, ...link }) => ({
          ...link,
          showTextOnPage: Boolean(link.showTextOnPage),
        }));

      const dataToSave = {
        ...sanitizedFormData,
        id: entity?.id, // Preserve ID if editing
        socialMedia: socialMediaToSave,
        customLinks: customLinksToSave,
        customLinksAboveSocial: Boolean(sanitizedFormData.customLinksAboveSocial),
      };

      try {
        // Save to Supabase database - use current user's ID if logged in
        const userId = currentUser?.id || null;
        const savedEntity = await saveEntity(dataToSave, userId);

        console.log('Form submitted:', savedEntity);
        clearProfileDraft(entity?.id || null);
        setSubmitted(true);
        
        setTimeout(() => {
          setSubmitted(false);
          setIsSubmitting(false);
          // Call onSave callback to notify parent
          if (onSave) {
            onSave();
          }
          // Reset form to initial state with default domains
          if (!entity) {
            setFormData(initialFormData);
          }
        }, 1500);
      } catch (error) {
        console.error('Error saving form:', error);
        setErrors({ general: error.message || 'An error occurred while saving. Please try again.' });
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="success-message">
        <div className="success-icon">✓</div>
        <h2>Profile Created Successfully!</h2>
        <p>Your profile has been created successfully.</p>
      </div>
    );
  }

  return (
    <div>
    <form onSubmit={handleSubmit} className="registration-form">
      <div className="form-section">
        <h2 className="section-title">Basic Information</h2>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="entityName">
              Company/Brand Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="entityName"
              name="entityName"
              value={formData.entityName}
              onChange={handleInputChange}
              className={errors.entityName ? 'error' : ''}
              placeholder="Enter your company or brand name"
              maxLength={100}
            />
            {errors.entityName && <span className="error-message">{errors.entityName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="contact@example.com"
              maxLength={254}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          {showOptional.logo && (
          <div className="form-group">
            <div className="optional-field-header">
              <label htmlFor="logo">Company/Brand Logo</label>
              {!formData.logo && (
                <button type="button" className="optional-hide" onClick={() => hideOptional('logo')}>Hide</button>
              )}
            </div>
            <div className="logo-upload-container">
              {logoPreview ? (
                <div className="logo-preview-wrapper">
                  <img src={logoPreview} alt="Logo preview" className="logo-preview" />
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="remove-logo-button"
                    title="Remove logo"
                  >
                    <FaTimes />
                  </button>
                </div>
              ) : (
                <label htmlFor="logo" className="logo-upload-label">
                  <FaImage className="upload-icon" />
                  <span>Choose Logo</span>
                  <input
                    type="file"
                    id="logo"
                    name="logo"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="logo-input"
                  />
                </label>
              )}
              {errors.logo && <span className="error-message">{errors.logo}</span>}
            </div>
          </div>
          )}

          {showOptional.description && (
          <div className="form-group full-width">
            <div className="optional-field-header">
              <label htmlFor="description">Description</label>
              {!formData.description && (
                <button type="button" className="optional-hide" onClick={() => hideOptional('description')}>Hide</button>
              )}
            </div>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="2"
              placeholder="Tell your story... Share what's in your heart"
              maxLength={2000}
            />
          </div>
          )}

          {showOptional.website && (
          <div className="form-group">
            <div className="optional-field-header">
              <label htmlFor="website">Website</label>
              {!formData.website && (
                <button type="button" className="optional-hide" onClick={() => hideOptional('website')}>Hide</button>
              )}
            </div>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className={errors.website ? 'error' : ''}
              placeholder="https://www.example.com"
              maxLength={500}
            />
            {errors.website && <span className="error-message">{errors.website}</span>}
          </div>
          )}

          {showOptional.phone && (
          <div className="form-group">
            <div className="optional-field-header">
              <label htmlFor="phone">Phone</label>
              {!formData.phone && (
                <button type="button" className="optional-hide" onClick={() => hideOptional('phone')}>Hide</button>
              )}
            </div>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+1 (555) 123-4567"
              maxLength={20}
            />
          </div>
          )}
        </div>

        <div className="optional-add-row">
          {!showOptional.logo && (
            <button type="button" className="optional-add-button" onClick={() => openOptional('logo')}>
              <FaPlus /> Logo
            </button>
          )}
          {!showOptional.description && (
            <button type="button" className="optional-add-button" onClick={() => openOptional('description')}>
              <FaPlus /> Description
            </button>
          )}
          {!showOptional.website && (
            <button type="button" className="optional-add-button" onClick={() => openOptional('website')}>
              <FaPlus /> Website
            </button>
          )}
          {!showOptional.phone && (
            <button type="button" className="optional-add-button" onClick={() => openOptional('phone')}>
              <FaPlus /> Phone
            </button>
          )}
          {!showOptional.contact && (
            <button type="button" className="optional-add-button" onClick={() => openOptional('contact')}>
              <FaPlus /> Contact person
            </button>
          )}
          {!showOptional.location && (
            <button type="button" className="optional-add-button" onClick={() => openOptional('location')}>
              <FaPlus /> Location
            </button>
          )}
          {!showOptional.social && (
            <button type="button" className={`optional-add-button${errors.linksRequired ? ' required-highlight' : ''}`} onClick={() => openOptional('social')}>
              <FaPlus /> Social media
            </button>
          )}
          {!showOptional.customLinks && (
            <button type="button" className={`optional-add-button${errors.linksRequired ? ' required-highlight' : ''}`} onClick={() => openOptional('customLinks')}>
              <FaPlus /> Custom links
            </button>
          )}
        </div>
        <p className={`links-required-hint${errors.linksRequired ? ' is-error' : ''}`}>
          At least one social media profile or custom link is required <span className="required">*</span>
        </p>
        {errors.linksRequired && (
          <span className="error-message">{errors.linksRequired}</span>
        )}
      </div>

      {showOptional.contact && (
      <div className="form-section">
        <div className="optional-field-header">
          <h2 className="section-title">Contact Person</h2>
          {!(formData.contactPersonName || formData.contactPersonEmail || formData.contactPersonPhone) && (
            <button type="button" className="optional-hide" onClick={() => hideOptional('contact')}>Hide</button>
          )}
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="contactPersonName">Name</label>
            <input
              type="text"
              id="contactPersonName"
              name="contactPersonName"
              value={formData.contactPersonName}
              onChange={handleInputChange}
              className={errors.contactPersonName ? 'error' : ''}
              placeholder="John Doe"
              maxLength={100}
            />
            {errors.contactPersonName && <span className="error-message">{errors.contactPersonName}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="contactPersonEmail">Email</label>
            <input
              type="email"
              id="contactPersonEmail"
              name="contactPersonEmail"
              value={formData.contactPersonEmail}
              onChange={handleInputChange}
              className={errors.contactPersonEmail ? 'error' : ''}
              placeholder="john.doe@example.com"
              maxLength={254}
            />
            {errors.contactPersonEmail && <span className="error-message">{errors.contactPersonEmail}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="contactPersonPhone">Phone</label>
            <input
              type="tel"
              id="contactPersonPhone"
              name="contactPersonPhone"
              value={formData.contactPersonPhone}
              onChange={handleInputChange}
              className={errors.contactPersonPhone ? 'error' : ''}
              placeholder="+1 (555) 123-4567"
              maxLength={20}
            />
            {errors.contactPersonPhone && <span className="error-message">{errors.contactPersonPhone}</span>}
          </div>
        </div>
      </div>
      )}

      {showOptional.location && (
      <div className="form-section">
        <div className="optional-field-header">
          <h2 className="section-title">Location</h2>
          {!(formData.address || formData.city || formData.country) && (
            <button type="button" className="optional-hide" onClick={() => hideOptional('location')}>Hide</button>
          )}
        </div>
        <div className="form-grid">
          <div className="form-group full-width">
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Street address"
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="City"
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label htmlFor="country">Country</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              placeholder="Country"
              maxLength={100}
            />
          </div>
        </div>
      </div>
      )}

      {showOptional.social && (
      <div className="form-section">
        <div className="optional-field-header">
          <h2 className="section-title">Social Media</h2>
          {addedSocial.length === 0 && (
            <button type="button" className="optional-hide" onClick={() => hideOptional('social')}>Hide</button>
          )}
        </div>
        <div className="social-picker">
          {socialMediaPlatforms
            .filter((platform) => !addedSocial.includes(platform.name.toLowerCase()))
            .map((platform) => {
              const Icon = platform.icon;
              const platformKey = platform.name.toLowerCase();
              return (
                <button
                  key={platformKey}
                  type="button"
                  className="social-picker-chip"
                  onClick={() => addSocialPlatform(platformKey)}
                  title={`Add ${platform.name}`}
                >
                  <Icon style={{ color: platform.color }} />
                  <span>{platform.name}</span>
                </button>
              );
            })}
        </div>
        {addedSocial.length > 0 && (
        <div className="social-media-grid">
          {socialMediaPlatforms
            .filter((platform) => addedSocial.includes(platform.name.toLowerCase()))
            .map((platform) => {
            const Icon = platform.icon;
            const platformKey = platform.name.toLowerCase();
            const url = formData.socialMedia[platformKey] || '';
            const errorKey = `social_${platformKey}`;

            return (
              <div key={platformKey} className="social-media-item">
                <div className="social-icon-wrapper" style={{ backgroundColor: `${platform.color}15` }}>
                  <Icon className="social-icon" style={{ color: platform.color }} />
                </div>
                <div className="social-input-wrapper">
                  <div className="social-input-header">
                    <label htmlFor={platformKey}>{platform.name}</label>
                    {platform.signupUrl && (
                      <a
                        href={platform.signupUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="create-account-link"
                        title={`Create ${platform.name} account`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaUserPlus className="create-account-icon" />
                        <span className="create-account-text">Create Account</span>
                        <FaExternalLinkAlt className="external-link-icon" />
                      </a>
                    )}
                    <button
                      type="button"
                      className="optional-hide"
                      onClick={() => removeSocialPlatform(platformKey)}
                      title={`Remove ${platform.name}`}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <input
                    type="url"
                    id={platformKey}
                    value={url}
                    onChange={(e) => handleSocialMediaChange(platformKey, e.target.value)}
                    className={errors[errorKey] ? 'error' : ''}
                    placeholder={platform.placeholder}
                    maxLength={500}
                  />
                  {errors[errorKey] && (
                    <span className="error-message-small">{errors[errorKey]}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
      )}

      {showOptional.customLinks && (
      <div className="form-section">
        <div className="optional-field-header">
          <h2 className="section-title">Custom Links</h2>
          {!(formData.customLinks.some((link) => link.name || link.icon || link.link) || formData.customLinksAboveSocial) && (
            <button type="button" className="optional-hide" onClick={() => hideOptional('customLinks')}>Hide</button>
          )}
        </div>
        <label className="custom-link-checkbox custom-links-layout-option">
          <input
            type="checkbox"
            checked={Boolean(formData.customLinksAboveSocial)}
            onChange={(e) => setFormData((prev) => ({
              ...prev,
              customLinksAboveSocial: e.target.checked,
            }))}
          />
          Display custom links above social media icons on the public page
        </label>
        <div className="custom-links-container">
          {formData.customLinks.map((customLink, index) => (
            <div key={customLink.clientId} className="custom-link-item">
              <div className="custom-link-item-header">
                <div className="custom-link-number">Link {index + 1}</div>
                <button
                  type="button"
                  className="custom-link-remove"
                  onClick={() => handleRemoveCustomLink(index)}
                  title="Remove this link"
                >
                  <FaTrash /> Remove
                </button>
              </div>
              <div className="custom-link-fields">
                <div className="form-group full-width">
                  <label htmlFor={`customLink_${index}_name`}>
                    Name
                  </label>
                  <input
                    type="text"
                    id={`customLink_${index}_name`}
                    value={customLink.name}
                    onChange={(e) => handleCustomLinkChange(index, 'name', e.target.value)}
                    className={errors[`customLink_${index}_name`] ? 'error' : ''}
                    placeholder="e.g., Donate, Volunteer"
                    maxLength={100}
                  />
                  {errors[`customLink_${index}_name`] && (
                    <span className="error-message">{errors[`customLink_${index}_name`]}</span>
                  )}
                </div>
                <div className="form-group full-width">
                  <label>
                    Icon
                  </label>
                  <p className="icon-picker-hint">Choose a nonprofit service icon or upload your own</p>
                  <div className="nonprofit-icon-grid" role="listbox" aria-label="Nonprofit service icons">
                    {NONPROFIT_ICONS.map((preset) => {
                      const Icon = preset.Icon;
                      const selected = customLink.icon === toPresetIconValue(preset.id);
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          className={`nonprofit-icon-option${selected ? ' selected' : ''}`}
                          onClick={() => handleSelectPresetIcon(index, preset.id)}
                          title={preset.label}
                        >
                          <Icon style={{ color: preset.color }} />
                          <span>{preset.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="icon-upload-container">
                    <div className="icon-upload-row">
                      {customLink.icon && !isPresetIcon(customLink.icon) && (
                        <div className="icon-preview-wrapper">
                          <CustomLinkIcon
                            icon={customLink.icon}
                            alt={`Custom link ${index + 1} icon`}
                            className="icon-preview"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveCustomLinkIcon(index)}
                            className="remove-icon-button"
                            title="Remove icon"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                      <label htmlFor={`customLink_${index}_icon`} className="icon-upload-label icon-upload-label-compact">
                        <FaImage className="upload-icon" />
                        <span>{customLink.icon && !isPresetIcon(customLink.icon) ? 'Replace image' : 'Upload image'}</span>
                        <input
                          type="file"
                          id={`customLink_${index}_icon`}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={(e) => handleCustomLinkIconChange(index, e)}
                          className="icon-upload-input"
                          style={{ display: 'none' }}
                        />
                      </label>
                      {customLink.icon && (
                        <button
                          type="button"
                          className="clear-icon-button"
                          onClick={() => handleRemoveCustomLinkIcon(index)}
                        >
                          Clear icon
                        </button>
                      )}
                    </div>
                    {errors[`customLink_${index}_icon`] && (
                      <span className="error-message">{errors[`customLink_${index}_icon`]}</span>
                    )}
                  </div>
                </div>
                <div className="form-group full-width">
                  <label htmlFor={`customLink_${index}_link`}>
                    Link URL
                  </label>
                  <input
                    type="url"
                    id={`customLink_${index}_link`}
                    value={customLink.link}
                    onChange={(e) => handleCustomLinkChange(index, 'link', e.target.value)}
                    className={errors[`customLink_${index}_link`] ? 'error' : ''}
                    placeholder="https://example.com"
                    maxLength={500}
                  />
                  {errors[`customLink_${index}_link`] && (
                    <span className="error-message">{errors[`customLink_${index}_link`]}</span>
                  )}
                </div>
                <div className="form-group full-width custom-link-show-text">
                  <label className="custom-link-checkbox">
                    <input
                      type="checkbox"
                      checked={Boolean(customLink.showTextOnPage)}
                      onChange={(e) => handleCustomLinkChange(index, 'showTextOnPage', e.target.checked)}
                    />
                    Show link name on public icons page
                  </label>
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="add-custom-link-button"
            onClick={handleAddCustomLink}
          >
            <FaPlus /> Add another link
          </button>
        </div>
      </div>
      )}

      <div className="form-actions">
        {(errors.general || errors.linksRequired) && (
          <div className="general-error-message">{errors.general || errors.linksRequired}</div>
        )}
        <div className="action-buttons">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="cancel-button"
            >
              Cancel
            </button>
          )}
          <button 
            type="submit" 
            className={`submit-button ${!hasFormChanged() || isSubmitting ? 'disabled' : ''}`}
            disabled={!hasFormChanged() || isSubmitting}
          >
            {isSubmitting 
              ? 'Submitting...' 
              : hasFormChanged() 
                ? (entity ? 'Update Entity' : 'Register Entity') 
                : 'No Changes Made'}
          </button>
        </div>
      </div>
    </form>
    </div>
  );
}

export default RegistrationForm;
